import { useState, type ReactNode } from 'react';
import { SiYoutube, SiYoutubeshorts } from 'react-icons/si';
import Tooltip from '@/components/Tooltip';
import { useI18nText } from '@/lib/utils/i18nText';
import { formatAbbrev } from '@/lib/utils/misc';
import { formatDurationSeconds } from '@/lib/utils/timezone';
import { getChannelTypeMetricIcon } from '@/lib/utils/channelPlatformMeta';
import { EmptyDash, formatNum } from '../../utils';
import type { Influencer } from '../../types';

/** Boxed grid card: [icon] [label  | value] — matches LastVideoCell `.lva-card`.
 *  Exported so the candidates page's non-YouTube rows share the exact same layout
 *  (icon + label + value, with the narrow-column label-hiding container query). */
export function MetricRow({ icon, label, value }: { icon: string | ReactNode; label: string; value: React.ReactNode }) {
  return (
    <div className="rav5-row">
      <Tooltip content={label}>
        <span className="rav5-icon" aria-hidden>
          {typeof icon === 'string' ? <i className={`fa fa-${icon}`} /> : icon}
        </span>
      </Tooltip>
      <div className="rav5-content">
        {/* Label truncates (and hides on very narrow columns via the container query)
            while the value stays full — the value is the data users read here. */}
        <span className="rav5-label">{label}</span>
        <span className="rav5-value">{value}</span>
      </div>
    </div>
  );
}

/** YouTube last-5 breakdown with a +/- toggle — collapsed shows views-only per kind
 *  (Normal/Shorts/Live); expanded shows views+likes+comments per kind (원본 GeneralViewership).
 *  Exported so the candidates page reuses the exact same layout instead of its own. */
export function YoutubeViewershipCell({
  last5, fn, t,
}: {
  last5: { videos?: { views?: number; likes?: number; comments?: number }; shorts?: { views?: number; likes?: number; comments?: number }; live?: { views?: number; likes?: number; comments?: number } };
  fn: (n?: number) => string;
  t: (en: string, ko?: string) => string;
}) {
  const [expanded, setExpanded] = useState(false);

  if (!expanded) {
    return (
      <div className="rav5-cell">
        <MetricRow icon={<SiYoutube size={11} />} label="Normal" value={fn(last5.videos?.views ?? 0)} />
        <MetricRow icon={<SiYoutubeshorts size={11} />} label="Shorts" value={fn(last5.shorts?.views ?? 0)} />
        <MetricRow icon="broadcast-tower" label="Live" value={fn(last5.live?.views ?? 0)} />
        <button type="button" className="ggc-inline-link rav5-toggle" onClick={() => setExpanded(true)}>
          {t('Details', '상세')}<i className="fa fa-angle-down" aria-hidden />
        </button>
      </div>
    );
  }

  const groups: Array<{ key: string; label: string; data?: { views?: number; likes?: number; comments?: number } }> = [
    { key: 'videos', label: t('Normal videos', '일반 동영상'), data: last5.videos },
    { key: 'shorts', label: t('Short videos', '쇼츠'), data: last5.shorts },
    { key: 'live', label: t('Live videos', '라이브'), data: last5.live },
  ];
  return (
    <div className="rav5-cell">
      {groups.map((g) => (
        <div key={g.key} className="rav5-group">
          <span className="rav5-group-label">{g.label}</span>
          <MetricRow icon={getChannelTypeMetricIcon('youtube', 'views')} label={t('Views', '조회수')} value={fn(g.data?.views ?? 0)} />
          <MetricRow icon={getChannelTypeMetricIcon('youtube', 'likes')} label={t('Likes', '좋아요')} value={fn(g.data?.likes ?? 0)} />
          <MetricRow icon={getChannelTypeMetricIcon('youtube', 'comments')} label={t('Comments', '댓글')} value={fn(g.data?.comments ?? 0)} />
        </div>
      ))}
      <button type="button" className="ggc-inline-link rav5-toggle" onClick={() => setExpanded(false)}>
        {t('Show less', '간략히')}<i className="fa fa-angle-up" aria-hidden />
      </button>
    </div>
  );
}

/** GeneralViewership 셀 (원본 GeneralViewership) - YouTube/Twitch/기타 채널별 상세 */
export function GeneralViewershipCell({ user, abbrev, showEmptyDash = true }: { user: Influencer; abbrev?: boolean; showEmptyDash?: boolean }) {
  const { t } = useI18nText();
  // Averaged last-5 stats can be fractional (e.g. 2,359.8) — round before display.
  const fn = (n?: number) => { if (!n) return '0'; const r = Math.round(n); return abbrev ? formatAbbrev(r, true) : formatNum(r); };
  const chType = user.channels?.[0]?.type ?? 'youtube';

  if (chType === 'youtube') {
    // v2 BE exposes the per-kind breakdown as `statsYoutubeBreakdown`; the legacy
    // name was `averageStatsYoutubeBreakdown`. Support both.
    const last5 = (user.statsYoutubeBreakdown?.last5 ?? user.averageStatsYoutubeBreakdown?.last5) as {
      videos?: { views?: number; likes?: number; comments?: number };
      shorts?: { views?: number; likes?: number; comments?: number };
      live?: { views?: number; likes?: number; comments?: number };
    } | undefined;
    const normViews = last5?.videos?.views;
    const shortViews = last5?.shorts?.views;
    const liveViews = last5?.live?.views;
    const hasAny = (normViews ?? 0) > 0 || (shortViews ?? 0) > 0 || (liveViews ?? 0) > 0;
    if (!hasAny) return showEmptyDash ? <EmptyDash /> : null;
    return <YoutubeViewershipCell last5={last5 ?? {}} fn={fn} t={t} />;
  }

  if (chType === 'twitch') {
    const stats = user.twitchStats;
    if (!stats) return showEmptyDash ? <EmptyDash /> : null;
    const durSec = stats.duration?.avg;
    const maxV = stats.maxViewers?.avg;
    const avgV = stats.avgViewers?.avg;
    const hasAny = durSec != null || maxV != null || avgV != null;
    if (!hasAny) return showEmptyDash ? <EmptyDash /> : null;
    return (
      <div className="rav5-cell">
        {durSec != null && <MetricRow icon="clock-o" label={t('Duration', '재생시간')} value={formatDurationSeconds(durSec / 1000)} />}
        {maxV != null && <MetricRow icon="user-circle" label={t('Max viewers', '최대 시청자')} value={fn(Math.round(maxV))} />}
        {avgV != null && <MetricRow icon="user-circle-o" label={t('Avg viewers', '평균 시청자')} value={fn(Math.round(avgV))} />}
      </div>
    );
  }

  if (chType === 'tiktok' || chType === 'douyin') {
    // v2 BE exposes recent stats as `stats.last5`; legacy name was `averageStats.last5`.
    const vs = user.stats?.last5 ?? user.averageStats?.last5;
    if (!vs) return showEmptyDash ? <EmptyDash /> : null;
    const hasAny = (vs.likes ?? 0) > 0 || (vs.views ?? 0) > 0 || (vs.comments ?? 0) > 0 || (vs.retweets ?? 0) > 0;
    if (!hasAny) return showEmptyDash ? <EmptyDash /> : null;
    return (
      <div className="rav5-cell">
        {vs.likes != null && <MetricRow icon={getChannelTypeMetricIcon(chType, 'likes')} label={t('Hearts', '좋아요')} value={fn(vs.likes)} />}
        {vs.views != null && <MetricRow icon={getChannelTypeMetricIcon(chType, 'views')} label={t('Views', '조회수')} value={fn(vs.views)} />}
        {vs.comments != null && <MetricRow icon={getChannelTypeMetricIcon(chType, 'comments')} label={t('Comments', '댓글')} value={fn(vs.comments)} />}
        {vs.retweets != null && <MetricRow icon={getChannelTypeMetricIcon(chType, 'shares')} label={t('Shares', '공유')} value={fn(vs.retweets)} />}
        {chType === 'douyin' && vs.saves != null && <MetricRow icon="bookmark" label={t('Saves', '저장')} value={fn(vs.saves)} />}
      </div>
    );
  }

  if (chType === 'instagram') {
    // v2 BE exposes recent stats as `stats.last5`; legacy name was `averageStats.last5`.
    const vs = user.stats?.last5 ?? user.averageStats?.last5;
    if (!vs) return showEmptyDash ? <EmptyDash /> : null;
    const hasAny = (vs.likes ?? 0) > 0 || (vs.views ?? 0) > 0 || (vs.comments ?? 0) > 0;
    if (!hasAny) return showEmptyDash ? <EmptyDash /> : null;
    return (
      <div className="rav5-cell">
        {vs.likes != null && <MetricRow icon={getChannelTypeMetricIcon('instagram', 'likes')} label={t('Likes', '좋아요')} value={fn(vs.likes)} />}
        {vs.views != null && <MetricRow icon={getChannelTypeMetricIcon('instagram', 'views')} label={t('Views', '조회수')} value={fn(vs.views)} />}
        {vs.comments != null && <MetricRow icon={getChannelTypeMetricIcon('instagram', 'comments')} label={t('Comments', '댓글')} value={fn(vs.comments)} />}
      </div>
    );
  }

  if (chType === 'twitter') {
    // v2 BE exposes recent stats as `stats.last5`; legacy name was `averageStats.last5`.
    const vs = user.stats?.last5 ?? user.averageStats?.last5;
    if (!vs) return showEmptyDash ? <EmptyDash /> : null;
    const hasAny = (vs.likes ?? 0) > 0 || (vs.retweets ?? 0) > 0 || (vs.replies ?? 0) > 0 || (vs.views ?? 0) > 0;
    if (!hasAny) return showEmptyDash ? <EmptyDash /> : null;
    return (
      <div className="rav5-cell">
        {vs.likes != null && <MetricRow icon={getChannelTypeMetricIcon('twitter', 'likes')} label={t('Likes', '좋아요')} value={fn(vs.likes)} />}
        {vs.retweets != null && <MetricRow icon={getChannelTypeMetricIcon('twitter', 'shares')} label={t('Retweets', '리트윗')} value={fn(vs.retweets)} />}
        {vs.replies != null && <MetricRow icon={getChannelTypeMetricIcon('twitter', 'replies')} label={t('Replies', '답글')} value={fn(vs.replies)} />}
        {vs.views != null && <MetricRow icon={getChannelTypeMetricIcon('twitter', 'views')} label={t('Views', '조회수')} value={fn(vs.views)} />}
      </div>
    );
  }

  if (chType === 'facebook') {
    // v2 BE exposes recent stats as `stats.last5`; legacy name was `averageStats.last5`.
    const vs = user.stats?.last5 ?? user.averageStats?.last5;
    if (!vs) return showEmptyDash ? <EmptyDash /> : null;
    const hasAny = (vs.likes ?? 0) > 0 || (vs.retweets ?? 0) > 0 || (vs.replies ?? 0) > 0;
    if (!hasAny) return showEmptyDash ? <EmptyDash /> : null;
    return (
      <div className="rav5-cell">
        {vs.likes != null && <MetricRow icon={getChannelTypeMetricIcon('facebook', 'likes')} label={t('Likes', '좋아요')} value={fn(vs.likes)} />}
        {vs.retweets != null && <MetricRow icon={getChannelTypeMetricIcon('facebook', 'shares')} label={t('Shares', '공유')} value={fn(vs.retweets)} />}
        {vs.replies != null && <MetricRow icon={getChannelTypeMetricIcon('facebook', 'replies')} label={t('Replies', '답글')} value={fn(vs.replies)} />}
      </div>
    );
  }

  if (['bilibili', 'chzzk', 'afreeca'].includes(chType)) {
    const bilibiliStats = (user as { bilibiliStats?: { views?: number } }).bilibiliStats;
    const chzzkStats = (user as { chzzkStats?: { duration?: { avg?: number }; maxViewers?: { avg?: number }; avgViewers?: { avg?: number } } }).chzzkStats;
    const afreecaStats = (user as { afreecaStats?: { duration?: { avg?: number }; maxViewers?: { avg?: number }; avgViewers?: { avg?: number } } }).afreecaStats;
    const stats = bilibiliStats ?? chzzkStats ?? afreecaStats;
    if (!stats) return showEmptyDash ? <EmptyDash /> : null;
    const dur = (stats as { duration?: { avg?: number } }).duration?.avg;
    const maxV = (stats as { maxViewers?: { avg?: number } }).maxViewers?.avg;
    const avgV = (stats as { avgViewers?: { avg?: number } }).avgViewers?.avg;
    const bViews = (stats as { views?: number }).views;
    const hasAny = dur != null || maxV != null || avgV != null || (bViews ?? 0) > 0;
    if (!hasAny) return showEmptyDash ? <EmptyDash /> : null;
    return (
      <div className="rav5-cell">
        {bViews != null && <MetricRow icon={getChannelTypeMetricIcon(chType, 'views')} label={t('Views', '조회수')} value={fn(bViews)} />}
        {dur != null && <MetricRow icon="clock-o" label={t('Duration', '재생시간')} value={formatDurationSeconds(dur / 1000)} />}
        {maxV != null && <MetricRow icon="user-circle" label={t('Max viewers', '최대 시청자')} value={fn(Math.round(maxV))} />}
        {avgV != null && <MetricRow icon="user-circle-o" label={t('Avg viewers', '평균 시청자')} value={fn(Math.round(avgV))} />}
      </div>
    );
  }

  return showEmptyDash ? <EmptyDash /> : null;
}
